import { useState } from 'react';
import { UserProfileForm } from './UserProfileForm';
import { CommunitySettingsForm } from './CommunitySettingsForm';
import { NotificationPreferencesForm } from './NotificationPreferencesForm';
import { PasswordChangeForm } from './PasswordChangeForm';
import AppearancePage from './AppearancePage';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { cn } from '@/lib/utils';
import { User, Palette, Lock } from 'lucide-react';

type AccountTab = 'profile' | 'appearance' | 'password';

export default function AccountPage() {
  const [activeTab, setActiveTab] = useState<AccountTab>('profile');

  const tabs: { key: AccountTab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { key: 'profile', label: 'Profile', icon: User },
    { key: 'appearance', label: 'Appearance', icon: Palette },
    { key: 'password', label: 'Password', icon: Lock },
  ];

  return (
    <div className="space-y-6 max-w-3xl pb-20 md:pb-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Account Settings</h1>
        <p className="text-gray-500 dark:text-gray-300">
          Manage your profile and account preferences
        </p>
      </div>

      {/* Pill Navigation */}
      <div className="w-full">
        <div className="w-full h-auto p-1 flex gap-1 bg-gray-100 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-bold transition-all min-h-[44px]',
                activeTab === tab.key
                  ? 'bg-white dark:bg-gray-950 shadow-md text-[#F37022]'
                  : 'text-gray-500 dark:text-gray-400'
              )}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'profile' && (
        <div className="space-y-6">
          {/* Profile Information Section */}
          <Card className="border dark:border-gray-700 shadow-sm dark:bg-gray-800">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-gray-900 dark:text-white">Profile Information</CardTitle>
              <CardDescription className="text-gray-500 dark:text-gray-300">
                Update your personal information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <UserProfileForm />
            </CardContent>
          </Card>

          {/* Community Settings Section */}
          <Card className="border dark:border-gray-700 shadow-sm dark:bg-gray-800">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-gray-900 dark:text-white">Community Settings <span className="text-sm font-medium text-orange-500">(Coming Soon)</span></CardTitle>
              <CardDescription className="text-gray-500 dark:text-gray-300">
                Manage how you appear in the community
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CommunitySettingsForm />
            </CardContent>
          </Card>

          {/* Notification Preferences Section */}
          <Card className="border dark:border-gray-700 shadow-sm dark:bg-gray-800">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-gray-900 dark:text-white">Notification Preferences <span className="text-sm font-medium text-orange-500">(Coming Soon)</span></CardTitle>
              <CardDescription className="text-gray-500 dark:text-gray-300">
                Control which community events send you email notifications
              </CardDescription>
            </CardHeader>
            <CardContent>
              <NotificationPreferencesForm />
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'appearance' && (
        <AppearancePage embedded />
      )}

      {activeTab === 'password' && (
        <Card className="border dark:border-gray-700 shadow-sm dark:bg-gray-800">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-gray-900 dark:text-white">Change Password</CardTitle>
            <CardDescription className="text-gray-500 dark:text-gray-300">
              Update your account password
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PasswordChangeForm />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
